import React, {useCallback, useEffect, useRef, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {FormattedMessage, useIntl} from "react-intl";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {ActionRow, ActionRowButton, Modal, useNotifications} from "peggirkit";
import {useRestaurant} from "@/context/restaurant/RestaurantProvider";
import {OptionGroup} from "@/data/entities/option";
import {ADMIN_MENU_QUERY_KEY, ProductOptionEditValues} from "@/data/entities/menuEditor";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {SetStateFn} from "@/utils/SetStateUtils";
import {OptionGroupsLookup} from "@widgets/menuEditor/optionGroups/optionGroupsLookup";
import OptionGroupFields from "@widgets/menuEditor/optionGroups/edit/OptionGroupFields";
import DraftOptionsList from "@widgets/menuEditor/optionGroups/edit/DraftOptionsList";
import NewOptionForm from "@widgets/menuEditor/optionGroups/edit/NewOptionForm";
import {
    DraftOption,
    newDraftKey,
    OptionGroupFormValues,
    saveOptionGroup,
    toDraftOptions,
    toOptionGroupFormValues,
} from "@widgets/menuEditor/optionGroups/edit/optionGroupDraft";

export type EditOptionGroupModalConfig = {
    show: boolean,
    group: OptionGroup | null, // `null` for a new group
};

type Props = {
    config: EditOptionGroupModalConfig,
    setConfig: SetStateFn<EditOptionGroupModalConfig>,
    lookup: OptionGroupsLookup,
};

/**
 * Edits an option group and its options. All changes are saved at once.
 */
const EditOptionGroupModal = ({config: {show, group}, setConfig, lookup}: Props) => {
    const intl = useIntl();
    const {notify} = useNotifications();
    const {dashboardLocation} = useRestaurant();
    const queryClient = useQueryClient();
    const formMethods = useForm<OptionGroupFormValues>({defaultValues: toOptionGroupFormValues(null)});
    const {reset, handleSubmit} = formMethods;
    const [drafts, setDrafts] = useState<DraftOption[]>([]);
    const [addingOption, setAddingOption] = useState(false);
    const wasShown = useRef(false);
    const onClose = useCallback(() => setConfig(prev => ({...prev, show: false})), [setConfig]);

    // Fill the modal when it opens (not on a refetch while it's open)
    useEffect(() => {
        if (show && !wasShown.current) {
            reset(toOptionGroupFormValues(group));
            setDrafts(toDraftOptions(group, lookup));
            setAddingOption(false);
        }
        wasShown.current = show;
    }, [show, group, lookup, reset]);

    const {mutate, isPending} = useMutation({
        mutationFn: (form: OptionGroupFormValues) => saveOptionGroup(
            dashboardLocation.id,
            group?.id ?? null,
            form,
            drafts,
        ),
        onSuccess: () => {
            onClose();
            notifySuccess(intl.formatMessage({id: "saved"}), notify);
        },
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
        // Also after an error: part of the changes may have been saved
        onSettled: () => queryClient.invalidateQueries({queryKey: [ADMIN_MENU_QUERY_KEY, dashboardLocation.id]}),
    });
    const submit = handleSubmit((form: OptionGroupFormValues) => mutate(form));

    return (
        <Modal
            show={show}
            onClose={onClose}
            title={intl.formatMessage({id: group === null ? "newOptionGroup" : "editOptionGroup"})}
            maxWidth={"max-w-2xl"}
            actionRow={
                <ActionRow>
                    <ActionRowButton
                        color={"secondary"}
                        disabled={isPending}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    onClose();
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"cancel"}/>
                    </ActionRowButton>

                    <ActionRowButton
                        // Finish (or cancel) the new option first
                        disabled={isPending || addingOption}
                        buttonAction={{
                            as: "button",
                            actionProps: {
                                onClick: e => {
                                    e.preventDefault();
                                    void submit();
                                },
                            },
                        }}
                    >
                        <FormattedMessage id={"saveAndClose"}/>
                    </ActionRowButton>
                </ActionRow>
            }
            disabled={isPending}
            loading={isPending}
            position={"center-bottom"}
            breakpoints={{md: {position: "center"}}}
        >
            <div className={"w-full flex flex-col gap-8 my-6 md:my-8"}>
                <FormProvider {...formMethods}>
                    <OptionGroupFields
                        onSubmit={submit}
                        disabled={isPending}
                    />
                </FormProvider>

                <DraftOptionsList
                    savedOptionIds={group?.optionIds ?? []}
                    drafts={drafts}
                    setDrafts={setDrafts}
                    lookup={lookup}
                    canAdd={!addingOption}
                    onAdd={() => setAddingOption(true)}
                    disabled={isPending}
                />

                {
                    addingOption &&
                    <NewOptionForm
                        onAdd={(values: ProductOptionEditValues) => {
                            setDrafts(prev => [...prev, {key: newDraftKey(), kind: "new", values}]);
                            setAddingOption(false);
                        }}
                        onCancel={() => setAddingOption(false)}
                    />
                }
            </div>
        </Modal>
    );
};

export default EditOptionGroupModal;